import React, { forwardRef, useCallback, useImperativeHandle, useRef } from 'react';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';

const OS_OPTIONS = {
  scrollbars: {
    autoHide: 'scroll',
    autoHideDelay: 600,
    theme: 'os-theme-dark',
  },
};

/**
 * Drop-in replacement for a scrollable <div>.
 * Renders an OverlayScrollbars-managed container and exposes a ref proxy
 * so callers can use ref.current.scrollTop, addEventListener, scrollTo, etc.
 * without any changes to existing scroll logic.
 *
 * When `disabled` is true, renders a plain element with no scrolling of its
 * own — useful when an ancestor already owns the scroll surface. The ref
 * proxy stays functional (it targets the rendered element), so callers that
 * use scrollTop / addEventListener keep working without branching.
 */
const ScrollContainer = forwardRef(
  ({ children, element = 'div', disabled = false, ...props }, ref) => {
    const osRef = useRef(null);
    const plainRef = useRef(null);

    const viewport = useCallback(
      () =>
        disabled
          ? plainRef.current
          : osRef.current?.osInstance()?.elements()?.viewport,
      [disabled]
    );

    useImperativeHandle(
      ref,
      () => ({
        get scrollTop() {
          return viewport()?.scrollTop ?? 0;
        },
        set scrollTop(val) {
          const vp = viewport();
          if (vp) vp.scrollTop = val;
        },
        get scrollHeight() {
          return viewport()?.scrollHeight ?? 0;
        },
        get clientHeight() {
          return viewport()?.clientHeight ?? 0;
        },
        addEventListener(...args) {
          viewport()?.addEventListener(...args);
        },
        removeEventListener(...args) {
          viewport()?.removeEventListener(...args);
        },
        scrollTo(...args) {
          viewport()?.scrollTo(...args);
        },
      }),
      [viewport]
    );

    if (disabled) {
      const Element = element;
      return (
        <Element ref={plainRef} {...props}>
          {children}
        </Element>
      );
    }

    return (
      <OverlayScrollbarsComponent
        ref={osRef}
        element={element}
        options={OS_OPTIONS}
        {...props}
      >
        {children}
      </OverlayScrollbarsComponent>
    );
  }
);

export default ScrollContainer;
